const _ = require('lodash')
const {isObjectNode, isArrayNode, getCombinator, mapAndClean} = require('../schemaConversionUtils')

const ROOT = ''
const SEPARATOR = '\\/'
const ARR_INDEX = '\\d+?'

/**
 * @param {string[]} path
 * @param {{schemaValueType?: string}} variableOptions
 * @returns {{path: string, schemaValueType?: string}}
 */
const buildVariableReferencePath = (path, variableOptions) => ({
    path: path.join(SEPARATOR),
    ...(variableOptions.schemaValueType !== undefined && {schemaValueType: variableOptions.schemaValueType})
})

/**
 * @param {Array<{path: string, schemaValueType?: string}>} paths
 * @returns {Array<{path: string, schemaValueType?: string}>}
 */
const uniqVariableReferencePaths = paths =>
    _.uniqBy(paths, ({path, schemaValueType}) => `${path}:${schemaValueType || ''}`)

/**
 * @param {*[]} assignedRefs
 * @param {*[]} srcRefs
 * @returns {*[]}
 */
const assignRefs = (assignedRefs = [], srcRefs = []) => _.concat(assignedRefs, srcRefs)

/**
 * Resolve a $ref reference to the actual schema
 * @param {string} ref
 * @param {Record<string, Record<string, *>>} allSchemas
 * @returns {*}
 */
const resolveRef = (ref, allSchemas) => {
    // Extract schema name from $ref (handles '#/SchemaName', 'SchemaName', '#/definitions/SchemaName' and 'definitions/SchemaName' formats)
    const schemaName = ref.split('/').pop()

    if (!schemaName) {
        return null
    }

    // Find the referenced schema in any of the schema files
    for (const fileSchemas of Object.values(allSchemas || {})) {
        if (fileSchemas?.[schemaName] || fileSchemas?.definitions?.[schemaName]) {
            return fileSchemas[schemaName] || fileSchemas.definitions[schemaName]
        }
    }

    return null
}

/**
 * @param {Record<string, Record<string, *>>} allSchemas - All schemas for resolving $ref references
 * @param {*} node
 * @param {string[]} path
 * @param {Set<string>} visited - Track visited $ref to prevent infinite loops
 * @param {Function} [getVariableOptions] - getVariableOptions(node.$ref) if provided, will return the variableOptions for this node. This is an alternative to allSchemas and dereferencing
 * @returns {*}
 */
const getNodeVariableRefs = (allSchemas, node = {}, path = [], visited = new Set(), getVariableOptions) => {
    const refs = {}

    // Handle $ref references with circular reference protection
    if (node.$ref) {
        if (visited.has(node.$ref)) {
            return refs // Circular reference detected, return empty refs
        }

        const resolvedNode = resolveRef(node.$ref, allSchemas)
        if (resolvedNode) {
            visited.add(node.$ref)
            const result = getNodeVariableRefs(allSchemas, resolvedNode, path, visited, getVariableOptions)
            visited.delete(node.$ref)
            return result
        } else if (getVariableOptions) {
            const variableOptions = getVariableOptions(node.$ref) || {}
            const {variableName} = variableOptions
            if (variableName) {
                if (!refs[variableName]) refs[variableName] = []
                refs[variableName].push(buildVariableReferencePath(path, variableOptions))
            }
        }
        return refs
    }

    const {variableOptions = {}} = node
    const {variableName} = variableOptions
    if (variableName) {
        if (!refs[variableName]) refs[variableName] = []
        refs[variableName].push(buildVariableReferencePath(path, variableOptions))
    }

    if (isObjectNode(node)) {
        const properties = node.properties || {}
        for (const [key, value] of Object.entries(properties)) {
            const propPath = _.clone(path)
            if (_.last(propPath) === ROOT) propPath.pop()
            propPath.push(key)
            _.assignWith(
                refs,
                getNodeVariableRefs(allSchemas, value, propPath, visited, getVariableOptions),
                assignRefs
            )
        }
    } else if (isArrayNode(node)) {
        _.assignWith(
            refs,
            getNodeVariableRefs(allSchemas, node.items, [...path, ARR_INDEX, ROOT], visited, getVariableOptions),
            assignRefs
        )
    }

    const combinator = getCombinator(node)
    if (combinator) {
        const combinatorItems = node[combinator]

        for (const item of combinatorItems) {
            _.assignWith(refs, getNodeVariableRefs(allSchemas, item, path, visited, getVariableOptions), assignRefs)
        }
    }

    return refs
}

/**
 *
 * @param {Record<string, Record<string, *>>} schemas
 * @param {Record<string, *>} schema
 * @param {Function} [getVariableOptions]
 * @returns {*}
 */
const buildVariableReferencePropertiesForSingleSchema = (schemas, schema, getVariableOptions) => {
    return _.mapValues(
        getNodeVariableRefs(schemas, schema, [], undefined, getVariableOptions),
        uniqVariableReferencePaths
    )
}
/**
 * @param {Record<string, Record<string, *>>} schemas
 * @param {Function} [getVariableOptions]
 * @returns {*}
 */
const buildVariableReferenceProperties = (schemas, getVariableOptions) =>
    mapAndClean(schemas, (/** @type {*} */ dataNodes) => {
        return mapAndClean(dataNodes, (/** @type {*} */ dataNode) => {
            if (!dataNode.isTopLevelDataNode) {
                return {}
            }

            return _.mapValues(
                getNodeVariableRefs(schemas, dataNode, [], undefined, getVariableOptions),
                uniqVariableReferencePaths
            )
        })
    })

module.exports = {
    buildVariableReferenceProperties,
    buildVariableReferencePropertiesForSingleSchema
}
